import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Alert,
  AlertTitle,
  Grid,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  Assignment as ContractIcon
} from '@mui/icons-material';

const VendorPatternCard = ({ vendor, patternType, onViewDetails, thresholds = null }) => {
  // Get risk data based on pattern type
  const getRiskData = () => {
    switch (patternType) {
      case 'split-purchase':
        return vendor.split_purchase_risk;
      case 'vendor-concentration':
        return vendor.concentration_risk;
      case 'repeat-awards':
        return vendor.repeat_award_risk;
      default:
        return null;
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'CRITICAL':
      case 'HIGH':
        return 'error';
      case 'MEDIUM':
        return 'warning';
      default:
        return 'primary';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  const riskData = getRiskData();
  const riskLevel = riskData?.risk_level || 'LOW';
  const riskColor = getRiskColor(riskLevel);

  return (
    <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: 'divider' }}>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" noWrap sx={{ maxWidth: '70%' }}>
              {vendor.vendor_name}
            </Typography>
            <Chip
              label={riskLevel}
              color={riskColor}
              size="small"
              icon={<WarningIcon />}
            />
          </Box>
        }
        subheader={
          <Box>
            <Box display="flex" gap={2} mt={1}>
              <Typography variant="caption" color="text.secondary">
                <ContractIcon sx={{ fontSize: 14, mr: 0.5 }} />
                {vendor.contract_count} contracts
              </Typography>
              <Typography variant="caption" color="text.secondary">
                <MoneyIcon sx={{ fontSize: 14, mr: 0.5 }} />
                {formatCurrency(vendor.total_amount)}
              </Typography>
            </Box>
            {vendor.vendor_location && (
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                <BusinessIcon sx={{ fontSize: 14, mr: 0.5 }} />
                {vendor.vendor_location.city}, {vendor.vendor_location.state}
              </Typography>
            )}
            {vendor.vendor_uei && (
              <Typography variant="caption" color="text.secondary" display="block">
                UEI: {vendor.vendor_uei}
              </Typography>
            )}
          </Box>
        }
      />
      
      <CardContent sx={{ flexGrow: 1 }}>
        {/* Pattern-specific content */}
        {patternType === 'split-purchase' && riskData?.patterns && (
          <Box>
            {riskData.patterns.slice(0, 2).map((pattern, index) => (
              <Alert 
                key={index} 
                severity="warning" 
                sx={{ mb: 1 }}
                icon={<ScheduleIcon />}
              >
                <AlertTitle sx={{ fontSize: '0.875rem' }}>
                  Potential Split to Avoid {pattern.nearest_threshold?.abbreviation}
                  {' '}({formatCurrency(pattern.nearest_threshold?.value)})
                </AlertTitle>
                <Typography variant="caption">
                  {pattern.contract_count} contracts in {pattern.time_span_hours.toFixed(0)}h
                  {' = '}
                  <strong>{formatCurrency(pattern.total_amount)}</strong>
                  {' '}
                  ({formatPercentage(pattern.nearest_threshold?.proximity * 100)} of threshold)
                </Typography>
                {pattern.same_agency && (
                  <Chip
                    label="Same Agency"
                    size="small"
                    color="warning"
                    sx={{ ml: 1, height: 20 }}
                  />
                )}
              </Alert>
            ))}
            {riskData.patterns.length > 2 && (
              <Typography variant="caption" color="text.secondary">
                +{riskData.patterns.length - 2} more patterns detected
              </Typography>
            )}
          </Box>
        )}

        {patternType === 'vendor-concentration' && riskData && (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Market Share
                </Typography>
                <Box display="flex" alignItems="center">
                  <Typography variant="h6">
                    {formatPercentage(riskData.market_share_percentage)}
                  </Typography>
                  <TrendingUpIcon color={riskColor} sx={{ ml: 1 }} />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(riskData.market_share_percentage, 100)}
                  color="primary"
                  sx={{ mt: 1 }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Contract Share
                </Typography>
                <Typography variant="h6">
                  {formatPercentage(riskData.contract_share_percentage)}
                </Typography>
                <Typography variant="caption">
                  {riskData.competitor_count} competitors
                </Typography>
              </Grid>
            </Grid>
            
            {riskData.dominant_naics && (
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Dominant NAICS: {riskData.dominant_naics.code}
                </Typography>
                <Typography variant="body2">
                  {formatPercentage(riskData.dominant_naics.percentage)} of contracts
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {patternType === 'repeat-awards' && riskData?.patterns && (
          <Box>
            <List dense>
              {riskData.patterns.slice(0, 2).map((pattern, index) => (
                <ListItem key={index} disableGutters>
                  <ListItemText
                    primary={
                      <Typography variant="body2" noWrap>
                        {pattern.product_service}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption">
                          {pattern.contract_count} contracts • 
                          {' '}{pattern.frequency_per_month.toFixed(1)}/month •
                          {' '}{pattern.time_pattern}
                        </Typography>
                        {pattern.average_competition < 2 && (
                          <Chip
                            label={`Avg ${pattern.average_competition.toFixed(1)} bidders`}
                            size="small"
                            color="error"
                            sx={{ ml: 1, height: 18 }}
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
            {riskData.patterns.length > 2 && (
              <Typography variant="caption" color="text.secondary">
                +{riskData.patterns.length - 2} more repeat patterns
              </Typography>
            )}
          </Box>
        )}

        {/* Agencies */}
        {vendor.agencies && vendor.agencies.length > 0 && (
          <Box mt={2}>
            <Typography variant="caption" color="text.secondary">
              <BusinessIcon sx={{ fontSize: 14, mr: 0.5 }} />
              Agencies:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
              {vendor.agencies.slice(0, 3).map((agency, index) => (
                <Chip
                  key={index}
                  label={agency.split(' ').slice(0, 3).join(' ')}
                  size="small"
                  variant="outlined"
                />
              ))}
              {vendor.agencies.length > 3 && (
                <Typography variant="caption" color="text.secondary">
                  +{vendor.agencies.length - 3}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Risk Score */}
        <Box mt={2}>
          <Typography variant="caption" color="text.secondary">
            Risk Score
          </Typography>
          <LinearProgress
            variant="determinate"
            value={vendor.risk_score || 0}
            color="primary"
            sx={{ mt: 0.5, height: 8, borderRadius: 1 }}
          />
          <Typography variant="caption" align="right" display="block">
            {vendor.risk_score || 0}/100
          </Typography>
        </Box>
      </CardContent>

      <Divider />
      
      <Box p={2}>
        <Button
          fullWidth
          variant="contained"
          size="small"
          onClick={() => onViewDetails(vendor)}
          color="primary"
        >
          View Pattern Contracts ({vendor.pattern_contracts ? vendor.pattern_contracts.length : vendor.contract_count})
        </Button>
      </Box>
    </Card>
  );
};

export default VendorPatternCard;